import { ClickAwayListener } from "@nova/component-click-away-listener"
import { zIndex, AddOns, ToolName } from "@nova/constants"
import * as Icons from "@nova/component-icons"
import { CreationMenuTab } from "./CreationMenuTab"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { HTMLAttributes, useEffect } from "react"
import { selectActiveToolName, setActiveTool } from "plugins/Toolbox/tool-registry"
import { selectSnapshotOpen, setSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { selectIsDomainWhiteListed, selectUserAddOns, selectUserID } from "user/profile/selectors"
import { usePluginState } from "panels/content/side-panel/usePluginState"
import { usePaywalls } from "features/entity-caching/licenses/useLicensePaywall"
import {
    clearSelection,
    expandSidePanel,
    selectIsSidePanelCollapsed,
    setActiveFeatureId,
    setActiveLayerId,
} from "panels/content/side-panel/slice.new"
import { useIsEditor } from "tools/hooks/useIsEditor"

export const CreationMenu = ({
    shouldIncludeTools,
    onBlur,
    shouldHide,
    fadeOutMs,
    ...rest
}: {
    shouldIncludeTools: boolean
    onBlur: () => void
    shouldHide?: boolean
    fadeOutMs?: number
} & HTMLAttributes<HTMLDivElement>) => {
    const dispatch = useAppDispatch()
    const { plugins, currentPlugin, setSelectedPlugin } = usePluginState()
    const activeTool = useAppSelector(selectActiveToolName())
    const userId = useAppSelector(selectUserID)

    const isEditor = useIsEditor()

    const pdfSnapshotIsOpen = useAppSelector(selectSnapshotOpen())

    const isSidePanelCollapsed = useAppSelector(selectIsSidePanelCollapsed)

    const availableAddOns = useAppSelector(selectUserAddOns)
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    const { showAddOnPaywallModal } = usePaywalls()

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation()
                onBlur()
            }
        }

        window.addEventListener("keydown", handleKeyDown)

        return () => {
            window.removeEventListener("keydown", handleKeyDown)
        }
    }, [onBlur])

    const activateTool = (tool: ToolName) =>
        dispatch(setActiveTool(activeTool !== tool ? tool : undefined))

    return (
        <ClickAwayListener onClickAway={onBlur} {...rest}>
            <div
                className={`absolute top-0 left-0 w-full bg-gray-900 border rounded-xl border-white-300 shadow-create-menu pt-2 px-4 pb-4 flex flex-col gap-px select-none transition-opacity ${shouldHide ? "opacity-0" : "opacity-100"}`}
                style={{
                    backdropFilter: "blur(8px)",
                    zIndex: zIndex.SidePanel + 2,
                    transitionDuration: `${fadeOutMs || 0}ms`,
                    ...rest.style,
                }}
            >
                {/* Features */}
                <small className="xs text-white-400 px-2 py-1.5">Features</small>
                {plugins.map((plugin, i) => {
                    const subscriptionType = plugin.subscriptionType
                    const hasAccess = subscriptionType
                        ? availableAddOns[subscriptionType as AddOns] || isWhitelisted
                        : true
                    return (
                        <CreationMenuTab
                            key={i}
                            data-tid={`create-${plugin.featureId}`}
                            iconUrl={plugin.iconUrl}
                            title={plugin.displayName}
                            onClick={() => {
                                if (!hasAccess && subscriptionType) {
                                    showAddOnPaywallModal(subscriptionType)
                                    return
                                }
                                if (!userId) return
                                setSelectedPlugin(plugin.featureId)
                                if (pdfSnapshotIsOpen) dispatch(setSnapshotOpen(false))

                                dispatch(setActiveLayerId({ id: undefined }))
                                if (!!plugin.onClick) plugin.onClick()
                                else if (plugin.createAction) {
                                    dispatch(
                                        plugin.createAction({
                                            data: {
                                                createdBy: { userId },
                                                skippedProcessing: false,
                                            },
                                        })
                                    )
                                }
                                dispatch(setActiveFeatureId({ feature: plugin.featureId }))
                                dispatch(clearSelection())
                                onBlur()
                            }}
                            isSelected={currentPlugin?.featureId === plugin.featureId}
                            isDisabled={!isEditor}
                            isPaywalled={!hasAccess}
                        />
                    )
                })}
                {/* PDF */}
                <CreationMenuTab
                    Icon={Icons.Map}
                    title="Generate PDF"
                    iconClasses="text-gray-0"
                    onClick={() => {
                        if (!pdfSnapshotIsOpen) {
                            if (currentPlugin) setSelectedPlugin(undefined)
                            if (activeTool) dispatch(setActiveTool(undefined))
                            dispatch(setSnapshotOpen(true))
                            dispatch(setActiveTool(ToolName.PDF))
                            if (isSidePanelCollapsed) {
                                dispatch(expandSidePanel())
                            }
                        } else {
                            dispatch(setSnapshotOpen(false))
                            dispatch(setActiveTool(undefined))
                        }
                    }}
                    isSelected={pdfSnapshotIsOpen}
                    shortcut={{ letters: ["M"] }}
                />

                {/* Tools */}
                {shouldIncludeTools && (
                    <>
                        <small className="xs text-white-400 px-2 py-1.5 mt-2">Tools</small>
                        <CreationMenuTab
                            Icon={Icons.PinDrop}
                            title="Point"
                            shortcut={{ letters: ["P"] }}
                            onClick={() => activateTool(ToolName.Icon)}
                            isDisabled={!isEditor}
                        />
                        <CreationMenuTab
                            Icon={Icons.Polyline}
                            title="Polygon"
                            shortcut={{ letters: ["L"] }}
                            onClick={() => activateTool(ToolName.Line)}
                            isDisabled={!isEditor}
                        />
                        <CreationMenuTab
                            Icon={Icons.InsertText}
                            title="Text"
                            shortcut={{ letters: ["T"] }}
                            onClick={() => activateTool(ToolName.Text)}
                            isDisabled={!isEditor}
                        />
                        <CreationMenuTab
                            Icon={Icons.Ruler}
                            title="Ruler"
                            shortcut={{ letters: ["R"] }}
                            onClick={() => activateTool(ToolName.Ruler)}
                        />
                        <CreationMenuTab
                            Icon={Icons.ChatBubble}
                            title="Comment"
                            shortcut={{ letters: ["C"] }}
                            onClick={() => activateTool(ToolName.Comment)}
                            isDisabled={!isEditor}
                        />
                        <CreationMenuTab
                            Icon={Icons.Upload}
                            title="Upload"
                            shortcut={{ letters: ["U"] }}
                            onClick={() => activateTool(ToolName.Upload)}
                            isDisabled={!isEditor}
                        />
                    </>
                )}
            </div>
        </ClickAwayListener>
    )
}
